// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a short list of choices for the Monday Madness card: a context menu on a computer and
// a bottom sheet on a phone, where context menus are switched off.

import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {MenuItemRadio} from '@app/features/ui/action_menu/MenuItemRadio';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import {MenuBottomSheet} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {isMobileExperienceEnabled} from '@app/features/ui/utils/MobileExperience';
import type React from 'react';
import {useState} from 'react';

export interface MondayChoice {
	key: string;
	label: string;
	icon?: React.ReactNode;
	/** Set on every choice to show them as a pick-one list with the current one marked. */
	selected?: boolean;
	onPick: () => void;
}

/** `show` opens the choices; render `sheet` once beside the buttons that call it. */
export function useMondayChoices() {
	const [open, setOpen] = useState<{title: string; choices: Array<MondayChoice>} | null>(null);
	/** `title` heads the phone sheet: what the choice is about, such as the row's text. */
	const show = (event: React.MouseEvent<HTMLElement>, title: string, choices: Array<MondayChoice>) => {
		if (isMobileExperienceEnabled()) {
			event.preventDefault();
			setOpen({title, choices});
			return;
		}
		ContextMenuCommands.openFromEvent(event, ({onClose}) => (
			<MenuGroup>
				{choices.map((choice) =>
					choice.selected === undefined ? (
						<MenuItem
							key={choice.key}
							icon={choice.icon}
							onClick={() => {
								choice.onPick();
								onClose();
							}}
						>
							{choice.label}
						</MenuItem>
					) : (
						<MenuItemRadio key={choice.key} selected={choice.selected} closeOnSelect onSelect={choice.onPick}>
							{choice.label}
						</MenuItemRadio>
					),
				)}
			</MenuGroup>
		));
	};
	const close = () => setOpen(null);
	const sheet = (
		<MenuBottomSheet
			isOpen={open !== null}
			onClose={close}
			title={open?.title}
			groups={[
				{
					items: (open?.choices ?? []).map((choice) =>
						choice.selected === undefined
							? {
									id: choice.key,
									icon: choice.icon,
									label: choice.label,
									onClick: () => {
										choice.onPick();
										close();
									},
								}
							: {
									label: choice.label,
									selected: choice.selected,
									onSelect: () => {
										choice.onPick();
										close();
									},
								},
					),
				},
			]}
		/>
	);
	return {show, sheet};
}
